import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Quiz-option button visual variants — mirror the Figma "Flashka Design
 * System" file `YZK9r3wDhvw1pY60KlVEIj`, section `3802:7759`
 * ("Quiz related buttons").
 *
 * The design is a 4 × 5 matrix:
 *   variant (columns): default | correct | incorrect | primary
 *   state   (rows)   : default | hover | pressed | focused | disabled
 * plus `skipped` (orange) — OUR addition, not in the Figma section; it carries
 * the legacy quiz option's 5th answer state (see the variant below) — and
 * `missed` (amber), the right option of a select-all the learner left out:
 * neither green nor red, so correct/wrong still only marks what they picked,
 * but a warm tint that stands out from an untouched option.
 *
 * Anatomy (every variant shares it):
 *   – A 24px-radius card (`rounded-3xl`) with a flat 1px border — NOT
 *     `rounded-full`: at the 52px resting height the two read the same, but a
 *     wrapped multi-line option would balloon into a stadium. NO bevel, NO
 *     shadow (unlike Button / Chip). Verified against the section crops.
 *   – A leading rounded-square badge (`size-7`, `rounded-[10px]`) holding the
 *     option letter (A / B / C …) in Mona Sans Bold 12 (token `L3`).
 *   – The option text in Mona Sans Medium 16 (token `B1`), left-aligned,
 *     free to wrap.
 *
 * State ladder (bg / border step one palette shade per state — read
 * token-for-token off the section):
 *   default  : {c}-100 / {c}-400   (neutral: secondary-50 / secondary-200)
 *   hover    : {c}-200 / {c}-500   (neutral: secondary-100 / secondary-200)
 *   pressed  : {c}-300 / {c}-600   (neutral: secondary-200 / secondary-300)
 *   focused  : rest colors + a per-variant focus ring (below)
 *   disabled : rest colors + `!opacity-30` (same treatment as Button)
 *
 * Focus ring shade (measured off the "Focused" row — the ring sits flush
 * OUTSIDE the resting border, which is kept):
 *   default   → secondary-600  (#525252)
 *   correct   → green-950       (#032e15)
 *   incorrect → red-950         (#460809)
 *   skipped   → orange-950
 *   missed    → amber-950
 *   primary   → secondary-600   (#525252)  — the violet variant uses the
 *               neutral gray ring, same as `default` (per the design).
 *   Web-only — `web:focus-visible:` is a no-op on RN.
 *
 * Dark mode is AUTOMATIC via the shared CSS-var palette (light in `:root`,
 * the mirror in `.dark` / `.dark:root`), on both web and native — so the
 * tokens below carry NO `dark:` swaps (same rationale as Button / Chip). The
 * solid badge fills use `-500`, the palindromic midpoint of the scale, which
 * is invariant under inversion; the white badge letter is a literal, also
 * invariant.
 */
export const quizButtonVariants = cva(
  // `items-start` + the text's `mt-px` (see quizButtonTextVariants) = the legacy
  // quiz-option behaviour: the letter badge stays optically centred on a
  // single-line option, and pins to the FIRST LINE once the text wraps —
  // instead of floating in the middle of a 3-line block. `py-3` (not the 2.5
  // the single-line frame implies) makes 12 + 28 + 12 = the 52px min height
  // exactly, so top-aligned content is still centred when it's one line.
  // NOT `items-baseline` (what legacy web uses): Yoga's baseline falls back to
  // node height for a nested View like the badge, so it breaks on RN.
  'group relative flex flex-row items-start gap-3 rounded-3xl border ' +
    'min-h-[52px] px-4 py-3 text-left ' +
    // Web-only colour transition (no transform/scale — the pressed state is a
    // colour step in Figma, not a depress). NativeWind ignores these on RN.
    'web:transition-[background-color,border-color,opacity] web:duration-150 web:ease-out ' +
    // Focus-ring SHAPE shared by every variant (1px, flush). The per-variant
    // colour is added on each variant class below.
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0',
  {
    variants: {
      variant: {
        // Neutral / unanswered option.
        default:
          'bg-secondary-50 border-secondary-200 ' +
          'enabled:hover:bg-secondary-100 ' +
          'enabled:active:bg-secondary-200 enabled:active:border-secondary-300 ' +
          'native:active:bg-secondary-200 native:active:border-secondary-300 ' +
          'web:focus-visible:ring-secondary-600 ' +
          // Dark: an option is a CONTROL — one step above its question card
          // (elevation.ts `DARK_CONTROL`), stepping up again on hover/press.
          'dark:bg-secondary-200/60 dark:enabled:hover:bg-secondary-200 dark:enabled:active:bg-secondary-300 ',
        // Correct answer (green).
        correct:
          'bg-green-100 border-green-400 ' +
          'enabled:hover:bg-green-200 enabled:hover:border-green-500 ' +
          'enabled:active:bg-green-300 enabled:active:border-green-600 ' +
          'native:active:bg-green-300 native:active:border-green-600 ' +
          'web:focus-visible:ring-green-950 ',
        // Incorrect answer (red).
        incorrect:
          'bg-red-100 border-red-400 ' +
          'enabled:hover:bg-red-200 enabled:hover:border-red-500 ' +
          'enabled:active:bg-red-300 enabled:active:border-red-600 ' +
          'native:active:bg-red-300 native:active:border-red-600 ' +
          'web:focus-visible:ring-red-950 ',
        // The right answer of a SKIPPED question (orange) — not in the Figma
        // section, added for the legacy quiz-option migration, which needs to
        // say "you skipped this; here it is" without claiming you got it
        // right. Same ladder as the others, on the DS `orange` ramp (not the
        // legacy `hard` one the old web option used).
        skipped:
          'bg-orange-100 border-orange-400 ' +
          'enabled:hover:bg-orange-200 enabled:hover:border-orange-500 ' +
          'enabled:active:bg-orange-300 enabled:active:border-orange-600 ' +
          'native:active:bg-orange-300 native:active:border-orange-600 ' +
          'web:focus-visible:ring-orange-950 ',
        // A right option the learner left out of a select-all (amber) — not in
        // the Figma section. Not green or red, so correct/wrong colour only
        // marks what was picked, but a warm tile that a "Missed" tag sits on.
        // Same ladder as the others, on the DS `amber` ramp.
        missed:
          'bg-amber-100 border-amber-400 ' +
          'enabled:hover:bg-amber-200 enabled:hover:border-amber-500 ' +
          'enabled:active:bg-amber-300 enabled:active:border-amber-600 ' +
          'native:active:bg-amber-300 native:active:border-amber-600 ' +
          'web:focus-visible:ring-amber-950 ',
        // Selected / highlighted option (violet).
        primary:
          'bg-primary-50 border-primary-400 ' +
          'enabled:hover:bg-primary-100 enabled:hover:border-primary-500 ' +
          'enabled:active:bg-primary-200 enabled:active:border-primary-500 ' +
          'native:active:bg-primary-200 native:active:border-primary-500 ' +
          'web:focus-visible:ring-secondary-600 ',
      },
      // Quiz options are full-width answer rows by default; opt out with
      // `fullWidth={false}` to hug content (as the Figma frames show them).
      fullWidth: {
        true: 'w-full',
        false: 'w-fit',
      },
      disabled: { true: '', false: '' },
    },
    compoundVariants: [
      // Disabled = default colours + opacity-30 (Figma "Disabled" row). `!`
      // overrides the Pressable primitive's built-in opacity-50 fallback.
      { disabled: true, class: '!opacity-30' },
    ],
    defaultVariants: {
      variant: 'default',
      fullWidth: true,
      disabled: false,
    },
  },
);

/**
 * Option-text classes per variant. Applied to the inner `<Text>` wrapping the
 * string child. `flex-1` lets it fill the row and wrap.
 *
 * The DS token `text-b1` (16/24) PINS the line height — not `leading-normal`,
 * which resolves from font metrics and lands differently on web vs RN, so no
 * single nudge could align both.
 *
 * `mt-px` BASELINE-aligns the first line with the badge letter. Centring the
 * two BOXES (the old `mt-0.5` = (28 − 24) / 2 = 2px) is what the Figma frame
 * measures, but it reads as misaligned: the eye tracks baselines, and a 12px
 * glyph centred in the 28px badge sits ~1.5px above a box-centred 16px one.
 * Mona Sans ascent/descent = 0.965 / 0.235 em, and the badge glyph's baseline
 * is independent of its own line height (it is flex-centred in the badge), so:
 *   badge baseline  = (28 − 16)/2 + (16 − 12·1.2)/2 + 12·0.965 = 18.38px
 *   label baseline  = mt + (24 − 16·1.2)/2 + 16·0.965         = mt + 17.84px
 * ⇒ mt = 0.54px, which `mt-px` rounds to. Same font on both platforms, and the
 * root rem is a fixed 16px, so the one value holds for web and RN alike.
 */
export const quizButtonTextVariants = cva(
  'mt-px flex-1 whitespace-normal text-left text-b1 font-medium native:font-mona-medium',
  {
    variants: {
      variant: {
        default: 'text-secondary-900',
        correct: 'text-green-950',
        incorrect: 'text-red-950',
        skipped: 'text-orange-950',
        missed: 'text-amber-950',
        primary: 'text-primary-950',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/**
 * Leading letter-badge box per variant. `correct` / `incorrect` / `skipped` /
 * `missed` are a `-500` fill ringed by the next shade down (`-600`, per the Figma badge
 * spec) with a white glyph; `default` / `primary` are outlined (tinted fill +
 * border + tinted glyph).
 */
export const quizBadgeVariants = cva(
  'flex size-7 shrink-0 items-center justify-center rounded-[10px] border',
  {
    variants: {
      variant: {
        default: 'bg-secondary-50 border-secondary-200 dark:bg-secondary-200',
        correct: 'bg-green-500 border-green-600',
        incorrect: 'bg-red-500 border-red-600',
        skipped: 'bg-orange-500 border-orange-600',
        missed: 'bg-amber-500 border-amber-600',
        primary: 'bg-primary-100 border-primary-400',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/**
 * Letter-badge glyph classes per variant — the DS token `text-l3` (Mona Sans
 * Bold 12/16). White on the solid correct/incorrect/skipped/missed fills; the
 * variant's darkest shade otherwise. `native:font-mona-bold` pins the RN
 * per-weight face (no-op on web, where the variable font respects
 * `font-bold`).
 *
 * NOT `leading-none`: a line box shorter than the font's natural one leaves RN
 * positioning the glyph off-centre inside the 28px badge (it looked fine on
 * web, where the flex centring hides it). The token's 16px line box centres on
 * both platforms.
 */
export const quizBadgeTextVariants = cva(
  'text-center text-l3 font-bold native:font-mona-bold',
  {
    variants: {
      variant: {
        default: 'text-secondary-900',
        correct: 'text-white',
        incorrect: 'text-white',
        skipped: 'text-white',
        missed: 'text-white',
        primary: 'text-primary-950',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export type QuizButtonVariantProps = VariantProps<typeof quizButtonVariants>;
